import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '@/stores/auth';
import { supabase } from '@/lib/supabase';
import { ref, readonly } from 'vue';
import { EventEmitter } from 'eventemitter3';
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';

class RealtimeClient extends EventEmitter {
  private static instance: RealtimeClient | null = null;
  private _socket: Socket | null = null;
  private _isConnected = ref(false);
  private _isConnecting = ref(false);
  private _refreshInFlight: Promise<string | null> | null = null;
  private _jobLogSubscriptions = new Set<string>();

  public readonly isConnected = readonly(this._isConnected);
  public readonly isConnecting = readonly(this._isConnecting);

  private constructor() {
    super();
    this.initialize();
    this.setupAuthSync();
    window.addEventListener('account-switched', (event) => {
      const accountId = (event as CustomEvent<{ accountId?: string }>).detail?.accountId;
      if (accountId) {
        this.switchAccount(accountId);
      }
    });
  }

  public static getInstance(): RealtimeClient {
    if (!RealtimeClient.instance) {
      RealtimeClient.instance = new RealtimeClient();
    }
    return RealtimeClient.instance;
  }

  private initialize() {
    const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;

    this._isConnecting.value = false;

    this._socket = io(apiUrl, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      auth: async (cb) => {
        console.log('RealtimeClient: Socket.io auth callback triggered');
        const authStore = useAuthStore();
        const token = await this.getValidAccessToken();
        if (token && authStore.activeAccountId) {
          console.log('RealtimeClient: Using token and active account for auth');
          cb({ token, accountId: authStore.activeAccountId });
        } else {
          console.error('RealtimeClient: Token or active account unavailable for socket auth');
          cb({});
        }
      },
      autoConnect: false,
      reconnection: true,
    });

    this._socket.on('connect', () => {
      this._isConnected.value = true;
      this._isConnecting.value = false;
      console.log('RealtimeClient: Connected to Realtime Server');
      this.resubscribeToJobLogs();
      this.emit('connect');
    });

    this._socket.on('disconnect', (reason) => {
      this._isConnected.value = false;
      this._isConnecting.value = false;
      console.log('RealtimeClient: Disconnected from Realtime Server', { reason });
      this.emit('disconnect', reason);
    });

    this._socket.on('connect_error', async (err: Error & { description?: unknown; context?: unknown }) => {
      this._isConnected.value = false;
      this._isConnecting.value = false;
      console.error('RealtimeClient: Connection error:', {
        message: err.message,
        description: err.description,
        context: err.context,
      });

      if (err.message?.includes('jwt expired')) {
        console.log('RealtimeClient: JWT expired, attempting token refresh...');

        const token = await this.refreshAccessToken();
        if (!token) {
          console.error('RealtimeClient: Failed to refresh token for reconnect');
          this._socket?.disconnect();
          this.emit('connect_error', err);
          return;
        }

        console.log('RealtimeClient: Token refreshed, reconnecting...');
        if (this._socket) {
          this._socket.auth = { token };
          this._socket.connect();
        }
        return;
      }

      if (err.message?.includes('Authentication')) {
        console.error('RealtimeClient: Server rejected the authenticated connection; not refreshing a valid session.');
        this._socket?.disconnect();
      }

      this.emit('connect_error', err);
    });

    // Forward all events from the socket to this EventEmitter instance
    this._socket.onAny((eventName, ...args) => {
      this.emit(eventName, ...args);
    });
  }

  private resubscribeToJobLogs(): void {
    if (!this._socket || this._jobLogSubscriptions.size === 0) {
      return;
    }

    for (const jobId of this._jobLogSubscriptions) {
      this._socket.emit('subscribe_to_job_logs', jobId);
    }
  }

  private async getValidAccessToken(): Promise<string | null> {
    const authStore = useAuthStore();
    const token = authStore.session?.access_token;

    if (token && !this.isJwtExpired(token)) {
      return token;
    }

    return this.refreshAccessToken();
  }

  private async refreshAccessToken(): Promise<string | null> {
    if (this._refreshInFlight) {
      return this._refreshInFlight;
    }

    this._refreshInFlight = (async () => {
      const { data: { session }, error } = await supabase.auth.refreshSession();
      if (error) {
        console.error('RealtimeClient: Failed to refresh session:', error);

        // If refresh fails, the session is likely dead.
        // AuthStore will handle the redirect via onAuthStateChange.
        return null;
      }

      return session?.access_token || null;
    })();

    try {
      return await this._refreshInFlight;
    } finally {
      this._refreshInFlight = null;
    }
  }

  private isJwtExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1])) as { exp?: number };
      if (!payload.exp) {
        return false;
      }

      return payload.exp * 1000 <= Date.now() + 30_000;
    } catch {
      return false;
    }
  }

  private setupAuthSync(): void {
    supabase.auth.onAuthStateChange((event: AuthChangeEvent, session: Session | null) => {
      if (!this._socket) {
        return;
      }

      if (session?.access_token) {
        const authStore = useAuthStore();
        this._socket.auth = {
          token: session.access_token,
          accountId: authStore.activeAccountId
        };
        return;
      }

      if (event === 'SIGNED_OUT') {
        this.disconnect();
      }
    });
  }

  public connect(): void {
    console.log('RealtimeClient: connect() called');
    const authStore = useAuthStore();
    const accountId = authStore.activeAccountId;

    if (!accountId) {
      console.log('RealtimeClient: Waiting for active account before connecting');
      return;
    }

    if (this._socket && !this._isConnected.value && !this._isConnecting.value) {
      this._socket.auth = {
        token: authStore.session?.access_token,
        accountId
      };
      this._isConnecting.value = true;
      this._socket.connect();
    }
  }

  public switchAccount(accountId: string): void {
    if (!this._socket) {
      return;
    }

    const authStore = useAuthStore();
    this._socket.auth = {
      token: authStore.session?.access_token,
      accountId
    };
    this._socket.disconnect();
    this._isConnected.value = false;
    this._isConnecting.value = false;
    this.connect();
  }

  public disconnect(): void {
    if (this._socket && this._isConnected.value) {
      this._socket.disconnect();
    }
  }

  public subscribeToJobLogs(jobId: string): void {
    this._jobLogSubscriptions.add(jobId);
    this._socket?.emit('subscribe_to_job_logs', jobId);
  }

  public unsubscribeFromJobLogs(jobId: string): void {
    this._jobLogSubscriptions.delete(jobId);
    this._socket?.emit('unsubscribe_from_job_logs', jobId);
  }

  public get socket(): Socket | null {
    return this._socket;
  }
}

export const realtimeClient = RealtimeClient.getInstance();
